import { Component, HostBinding, OnDestroy, OnInit } from "@angular/core";
import { HttpErrorResponse } from "@angular/common/http";
import { FormControl, FormGroup } from "@angular/forms";
import { PageEvent } from "@angular/material/paginator";
import { Sort } from "@angular/material/sort";
import moment from "moment";
import { Subject, of } from "rxjs";
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  switchMap,
  take,
  takeUntil,
  tap,
} from "rxjs/operators";
import { TranslateService } from "@ngx-translate/core";
import { DigitalCardService } from "../digital-card.service";
import type {
  DigitalCardDomainAnalytics,
  DomainStatsRow,
} from "../digital-cards.interface";

const MEDIUM_LABELS: Record<string, string> = {
  share: "digitalCards.stats.mediums.share",
  qr: "digitalCards.stats.mediums.qr",
  qr_meet: "digitalCards.stats.mediums.qrMeet",
  qr_background: "digitalCards.stats.mediums.qrBackground",
  qr_print: "digitalCards.stats.mediums.qrPrint",
  qr_paper: "digitalCards.stats.mediums.qrPaper",
  qr_plastic: "digitalCards.stats.mediums.qrPlastic",
  nfc: "digitalCards.stats.mediums.nfc",
  nfc_paper: "digitalCards.stats.mediums.nfcPaper",
  nfc_plastic: "digitalCards.stats.mediums.nfcPlastic",
  email: "digitalCards.stats.mediums.email",
  whatsapp: "digitalCards.stats.mediums.whatsapp",
  facebook: "digitalCards.stats.mediums.facebook",
  x: "digitalCards.stats.mediums.x",
  linkedin: "digitalCards.stats.mediums.linkedin",
  signature: "digitalCards.stats.mediums.signature",
  sms: "digitalCards.stats.mediums.sms",
  social: "digitalCards.stats.mediums.social",
  print: "digitalCards.stats.mediums.print",
  wallet: "digitalCards.stats.mediums.wallet",
  other: "digitalCards.stats.mediums.other",
  unknown: "digitalCards.stats.mediums.unknown",
};

const ELEMENT_LABELS: Record<string, string> = {
  company_logo: "digitalCards.stats.elements.companyLogo",
  company_www: "digitalCards.stats.elements.companyWww",
  phone: "digitalCards.stats.elements.phone",
  mobile: "digitalCards.stats.elements.mobile",
  email: "digitalCards.stats.elements.email",
  save_contact: "digitalCards.stats.elements.saveContact",
  pronounce_name: "digitalCards.stats.elements.pronounceName",
  book_meeting: "digitalCards.stats.elements.bookMeeting",
  calendar: "digitalCards.stats.elements.calendar",
  custom_link: "digitalCards.stats.elements.customLink",
  dynamic_var: "digitalCards.stats.elements.dynamicVar",
};

const MEDIUM_ORDER = [
  "share",
  "qr",
  "qr_meet",
  "qr_background",
  "qr_print",
  "qr_paper",
  "qr_plastic",
  "nfc",
  "nfc_paper",
  "nfc_plastic",
  "email",
  "whatsapp",
  "facebook",
  "x",
  "linkedin",
  "signature",
  "sms",
  "social",
  "print",
  "wallet",
  "other",
  "unknown",
] as const;

const ELEMENT_ORDER = [
  "company_logo",
  "company_www",
  "phone",
  "mobile",
  "email",
  "save_contact",
  "pronounce_name",
  "book_meeting",
  "calendar",
  "custom_link",
  "dynamic_var",
] as const;

const HIDDEN_MEDIUMS = new Set([
  "apple_wallet",
  "google_wallet",
  "applewallet",
  "googlewallet",
]);

const EMPTY_ANALYTICS: DigitalCardDomainAnalytics = {
  summary: {
    totalLandings: 0,
    totalInteractions: 0,
    totalSourceHits: 0,
    totalPreviews: 0,
    totalSavedContacts: 0,
  },
  columns: {
    mediums: [],
    mediumLabels: {},
    savedContactForm: false,
    elements: [],
  },
  overview: {
    cards: 0,
    sources: [],
    elements: [],
    savedContacts: 0,
    sourceHits: 0,
    interactions: 0,
    visits: 0,
  },
  data: [],
  total: 0,
};

type BreakdownKind = "campaign" | "channel" | "element";

type BreakdownItem = {
  key: string;
  label: string;
  count: number;
  pct: number;
  ofVisitsPct: number;
  kind: BreakdownKind;
};

export type StatsTableRow = DomainStatsRow & {
  rowId: string;
  sourcesTotal: number;
  interactionsTotal: number;
  campaignSourcesTotal: number;
  channelSourcesTotal: number;
  interactionRate: number;
  contactRate: number;
  topSources: BreakdownItem[];
  sourceBreakdown: BreakdownItem[];
  clickBreakdown: BreakdownItem[];
  topSource: BreakdownItem | null;
  topClick: BreakdownItem | null;
  isExpanded: boolean;
  hasDetails: boolean;
};

@Component({
  selector: "app-digital-card-statistics",
  templateUrl: "./digital-card-statistics.component.html",
  styleUrls: ["./digital-card-statistics.component.scss"],
  standalone: false,
})
export class DigitalCardStatisticsComponent implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();
  private readonly reload$ = new Subject<"full" | "table">();

  sortBy = "visits";
  sortDirection: "asc" | "desc" = "desc";
  readonly displayedColumns = [
    "expand",
    "employee",
    "cardName",
    "visits",
    "sources",
    "interactions",
    "contacts",
  ];
  readonly detailColumns = ["expandedDetail"];
  tableRows: StatsTableRow[] = [];
  overviewSources: BreakdownItem[] = [];
  overviewClicks: BreakdownItem[] = [];
  overviewCards = 0;
  overviewContacts = 0;
  overviewSourceHits = 0;
  overviewInteractions = 0;
  overviewVisits = 0;
  overviewTopSource: BreakdownItem | null = null;
  overviewTopClick: BreakdownItem | null = null;
  overviewInteractionRate = 0;
  readonly pageSizeOptions = [25, 50, 100];
  pageSize = 50;
  pageIndex = 0;
  totalRows = 0;
  data: DigitalCardDomainAnalytics | null = null;
  mediumLabels: Record<string, string> = {};
  tableLoading = false;
  summaryLoading = false;
  searchControl = new FormControl<string>("", { nonNullable: true });
  dateRangeForm = new FormGroup({
    from: new FormControl(moment().startOf("month").toDate()),
    to: new FormControl(moment().toDate()),
  });

  @HostBinding("class.is-empty")
  get isEmpty(): boolean {
    return !this.tableLoading && this.totalRows === 0 && !this.isFilterActive;
  }

  private get searchPhrase(): string {
    return this.searchControl.value.trim();
  }

  get isFilterActive(): boolean {
    return Boolean(this.searchPhrase);
  }

  get showNoSearchResults(): boolean {
    return !this.tableLoading && this.isFilterActive && this.totalRows === 0;
  }

  get showDomainOverview(): boolean {
    if (this.summaryLoading) return true;
    return (
      this.overviewCards > 0 ||
      this.overviewVisits > 0 ||
      this.overviewSourceHits > 0 ||
      this.overviewInteractions > 0 ||
      this.overviewContacts > 0 ||
      this.overviewSources.length > 0 ||
      this.overviewClicks.length > 0
    );
  }

  constructor(
    private readonly digitalCardService: DigitalCardService,
    private readonly translate: TranslateService,
  ) {}

  ngOnInit(): void {
    this.reload$
      .pipe(
        switchMap((mode) => {
          this.tableLoading = true;
          if (mode === "full") this.summaryLoading = true;

          const { from, to } = this.dateRangeForm.getRawValue();
          const fromDate = from ? moment(from) : null;
          const toDate = to ? moment(to) : null;
          if (!fromDate?.isValid() || !toDate?.isValid()) {
            this.tableLoading = false;
            if (mode === "full") this.summaryLoading = false;
            return of(null);
          }

          return this.digitalCardService
            .getDomainAnalytics({
              from: fromDate.format("YYYY-MM-DD"),
              to: toDate.format("YYYY-MM-DD"),
              page: this.pageIndex + 1,
              limit: this.pageSize,
              search: this.searchPhrase || undefined,
              sortBy: this.sortBy,
              order: this.sortDirection === "asc" ? "ASC" : "DESC",
            })
            .pipe(
              tap({
                next: (res) => this.applyResponse(res, mode),
              }),
              catchError((err: unknown) => {
                if (this.isEmptyAnalyticsError(err)) {
                  this.applyResponse(EMPTY_ANALYTICS, mode);
                  return of(null);
                }
                this.tableLoading = false;
                if (mode === "full") this.summaryLoading = false;
                console.error("Failed to load analytics", err);
                return of(null);
              }),
            );
        }),
        takeUntil(this.destroy$),
      )
      .subscribe();

    this.searchControl.valueChanges
      .pipe(debounceTime(300), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(() => {
        this.pageIndex = 0;
        this.loadData("full");
      });

    this.dateRangeForm.valueChanges
      .pipe(debounceTime(0), takeUntil(this.destroy$))
      .subscribe(() => {
        const { from, to } = this.dateRangeForm.getRawValue();
        if (from && to && moment(from).isValid() && moment(to).isValid()) {
          this.pageIndex = 0;
          this.loadData("full");
        }
      });

    this.loadData("full");
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.reload$.complete();
  }

  loadData(mode: "full" | "table" = "full"): void {
    this.reload$.next(mode);
  }

  onSortChange(event: Sort): void {
    this.sortBy = event.active || "visits";
    this.sortDirection = event.direction === "asc" ? "asc" : "desc";
    this.pageIndex = 0;
    this.loadData("table");
  }

  onPage(event: PageEvent): void {
    this.pageSize = event.pageSize;
    this.pageIndex = event.pageIndex;
    this.loadData("table");
  }

  toggleRow(row: StatsTableRow, event?: Event): void {
    event?.stopPropagation();
    if (!row.hasDetails) return;
    row.isExpanded = !row.isExpanded;
  }

  exportCsv(): void {
    const { from, to } = this.dateRangeForm.getRawValue();
    const fromStr =
      from && moment(from).isValid() ? moment(from).format("YYYY-MM-DD") : "";
    const toStr =
      to && moment(to).isValid() ? moment(to).format("YYYY-MM-DD") : "";
    const rawLang = (this.translate.currentLang || "en").split("-")[0];
    const lang = ["pl", "en", "es"].includes(rawLang) ? rawLang : "en";
    this.digitalCardService
      .downloadDomainAnalyticsCsv(fromStr, toStr, lang)
      .pipe(takeUntil(this.destroy$), take(1))
      .subscribe({
        next: (blob) => {
          const url = URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = url;
          a.download = `digital-cards-domain-stats_${fromStr}_${toStr}.csv`;
          a.style.visibility = "hidden";
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          URL.revokeObjectURL(url);
        },
      });
  }

  private applyResponse(
    res: DigitalCardDomainAnalytics,
    mode: "full" | "table",
  ): void {
    if (mode === "full" || this.data == null) {
      this.data = res;
      this.applyOverview(res);
    }
    this.mediumLabels = res.columns?.mediumLabels ?? this.mediumLabels;
    const expanded = new Set(
      this.tableRows.filter((row) => row.isExpanded).map((row) => row.rowId),
    );
    this.tableRows = (res.data ?? []).map((row, index) =>
      this.toTableRow(row, index, expanded),
    );
    this.totalRows = typeof res.total === "number" ? res.total : 0;
    this.tableLoading = false;
    if (mode === "full") this.summaryLoading = false;
  }

  private applyOverview(res: DigitalCardDomainAnalytics): void {
    const visits = res.overview?.visits ?? res.summary?.totalLandings ?? 0;
    this.overviewCards = res.overview?.cards ?? res.total ?? 0;
    this.overviewContacts =
      res.overview?.savedContacts ?? res.summary?.totalSavedContacts ?? 0;
    this.overviewSources = this.buildBreakdown(
      this.countsFromList(res.overview?.sources),
      "medium",
      MEDIUM_ORDER,
      visits,
    );
    this.overviewClicks = this.buildBreakdown(
      this.countsFromList(res.overview?.elements),
      "element",
      ELEMENT_ORDER,
      visits,
    );
    this.overviewSourceHits =
      res.overview?.sourceHits ??
      this.overviewSources.reduce((sum, item) => sum + item.count, 0);
    this.overviewInteractions =
      res.overview?.interactions ??
      this.overviewClicks.reduce((sum, item) => sum + item.count, 0);
    this.overviewVisits = visits;
    this.overviewTopSource = this.overviewSources[0] ?? null;
    this.overviewTopClick = this.overviewClicks[0] ?? null;
    this.overviewInteractionRate = this.ratePct(
      this.overviewInteractions,
      visits,
    );
  }

  private toTableRow(
    row: DomainStatsRow,
    index: number,
    expanded: Set<string>,
  ): StatsTableRow {
    const rowId = `${row.employee}::${row.cardName}::${index}`;
    const visits = row.visits ?? 0;
    const sourceBreakdown = this.buildBreakdown(
      row.mediumCounts,
      "medium",
      MEDIUM_ORDER,
      visits,
    );
    const clickBreakdown = this.buildBreakdown(
      row.elementCounts,
      "element",
      ELEMENT_ORDER,
      visits,
    );
    let sourcesTotal = 0;
    let campaignSourcesTotal = 0;
    let channelSourcesTotal = 0;
    for (const item of sourceBreakdown) {
      sourcesTotal += item.count;
      if (item.kind === "campaign") campaignSourcesTotal += item.count;
      else channelSourcesTotal += item.count;
    }
    const interactionsTotal = clickBreakdown.reduce(
      (sum, item) => sum + item.count,
      0,
    );
    const contacts = row.savedContactForms ?? 0;
    return {
      ...row,
      rowId,
      sourcesTotal,
      interactionsTotal,
      campaignSourcesTotal,
      channelSourcesTotal,
      interactionRate: this.ratePct(interactionsTotal, visits),
      contactRate: this.ratePct(contacts, visits),
      topSources: sourceBreakdown.slice(0, 2),
      sourceBreakdown,
      clickBreakdown,
      topSource: sourceBreakdown[0] ?? null,
      topClick: clickBreakdown[0] ?? null,
      isExpanded: expanded.has(rowId),
      hasDetails:
        visits > 0 ||
        sourceBreakdown.length > 0 ||
        clickBreakdown.length > 0 ||
        contacts > 0,
    };
  }

  private countsFromList(
    items: { key: string; count: number }[] | null | undefined,
  ): Record<string, number> {
    const counts: Record<string, number> = {};
    for (const item of items ?? []) {
      if (!item?.key || !item.count || item.count <= 0) continue;
      counts[item.key] = (counts[item.key] ?? 0) + item.count;
    }
    return counts;
  }

  private buildBreakdown(
    counts: Record<string, number> | null | undefined,
    kind: "medium" | "element",
    preferred: readonly string[],
    visits: number,
  ): BreakdownItem[] {
    const orderIndex = new Map(
      preferred.map((key, index) => [key.toLowerCase(), index]),
    );
    const entries: BreakdownItem[] = [];
    let total = 0;

    for (const [key, count] of Object.entries(counts ?? {})) {
      if (!count || count <= 0) continue;
      if (kind === "medium" && HIDDEN_MEDIUMS.has(key.toLowerCase())) continue;
      total += count;
      entries.push({
        key,
        label: this.breakdownLabel(kind, key),
        count,
        pct: 0,
        ofVisitsPct: this.ratePct(count, visits),
        kind: this.breakdownKind(kind, key),
      });
    }

    for (const item of entries) {
      item.pct = total > 0 ? Math.round((item.count / total) * 100) : 0;
    }

    entries.sort((a, b) => {
      if (b.count !== a.count) return b.count - a.count;
      const ai = orderIndex.get(a.key.toLowerCase()) ?? 999;
      const bi = orderIndex.get(b.key.toLowerCase()) ?? 999;
      if (ai !== bi) return ai - bi;
      return a.label.localeCompare(b.label);
    });
    return entries;
  }

  private breakdownKind(
    kind: "medium" | "element",
    key: string,
  ): BreakdownKind {
    if (kind === "element") return "element";
    const campaign =
      this.mediumLabels[key] ?? this.mediumLabels[key.toLowerCase()] ?? "";
    if (campaign.trim() || key.toLowerCase().startsWith("cs_")) {
      return "campaign";
    }
    return "channel";
  }

  private breakdownLabel(kind: "medium" | "element", key: string): string {
    if (kind === "element") {
      const labelKey = ELEMENT_LABELS[key] ?? ELEMENT_LABELS[key.toLowerCase()];
      return labelKey ? (this.translate.instant(labelKey) as string) : key;
    }
    const campaign =
      this.mediumLabels[key] ?? this.mediumLabels[key.toLowerCase()] ?? "";
    if (campaign.trim() || key.toLowerCase().startsWith("cs_")) {
      const name =
        campaign.trim() ||
        (this.translate.instant("digitalCards.stats.orphanCampaign") as string);
      return name;
    }
    const labelKey =
      MEDIUM_LABELS[key.toLowerCase()] ?? "digitalCards.stats.mediums.unknown";
    return this.translate.instant(labelKey) as string;
  }

  private ratePct(part: number, whole: number): number {
    if (!whole || whole <= 0 || !part || part <= 0) return 0;
    return Math.round((part / whole) * 1000) / 10;
  }

  private isEmptyAnalyticsError(err: unknown): boolean {
    if (!(err instanceof HttpErrorResponse)) return false;
    if ([404, 422].includes(err.status)) return true;
    const payload = err.error as {
      errorKey?: unknown;
      message?: unknown;
    } | null;
    const errorKey =
      typeof payload?.errorKey === "string"
        ? payload.errorKey.toLowerCase()
        : "";
    const message =
      typeof payload?.message === "string" ? payload.message.toLowerCase() : "";
    return (
      errorKey.includes("not_found") ||
      errorKey.includes("no_data") ||
      errorKey.includes("no_profile") ||
      message.includes("not found") ||
      message.includes("no data") ||
      message.includes("no profile")
    );
  }
}
